// One order card in the fanned-out family. The card shows what it does, what it costs on the command
// net, and - when the selected unit cannot play it - why it is greyed out (spec §5).

import type { CardDef } from '../../sim/types';
import type { FamilyMeta } from '../../sim/deck';
import { CostMarks, ShapeTile } from './ui';

export function OrderCard({
  def, meta, issueSec, txSec, execSec, reason, waitSec, active, onTap,
}: {
  def: CardDef;
  meta: FamilyMeta;
  issueSec: number;
  txSec: number;
  execSec: number;
  /** Set when the card cannot be played by the selected unit; the card dims and the reason is its tooltip. */
  reason?: string;
  /** Seconds the command net would make this order wait; shown when the net is saturated. */
  waitSec: number;
  active: boolean;
  onTap: () => void;
}) {
  const disabled = reason !== undefined;
  return (
    <button
      type="button"
      data-testid={`card-${def.id}`}
      data-disabled={disabled ? 'true' : 'false'}
      disabled={disabled}
      title={reason ?? def.blurb}
      onClick={onTap}
      className="wt-card fade-in flex w-full items-center gap-2 text-left"
      style={{
        borderColor: active ? meta.color : 'var(--color-slate-700)',
        background: active ? `color-mix(in srgb, ${meta.color} 14%, var(--color-slate-800))` : 'var(--color-slate-800)',
        opacity: disabled ? 0.38 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        // Card edge glow carries the cost state: ready, queued behind the net, or saturated.
        boxShadow: active ? `0 0 0 1px ${meta.color}` : waitSec > 0 ? '0 0 0 1px var(--color-warn)' : 'none',
      }}
    >
      {/* The order card wears its family's shape as a solid mark, small and quiet. */}
      <ShapeTile meta={meta} size={16} active={active} solid />
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline justify-between gap-2">
          <span className="truncate text-[12px] font-medium text-paper">{def.label}</span>
          {waitSec > 0 && <span className="mono shrink-0 text-[9px] text-warn">+{Math.round(waitSec)}s wait</span>}
        </span>
        <span className="mono block truncate text-[10px] leading-tight text-fog">{def.blurb}</span>
        <span className="mt-1 block">
          <CostMarks issueSec={issueSec} txSec={txSec} execSec={execSec} color={meta.color} />
        </span>
      </span>
    </button>
  );
}
